import { FC } from 'react';
import {
  BreadcrumbItem,
  BreadcrumbLink,
  Breadcrumb as ChakraBreadcrumb,
  Text,
} from '@chakra-ui/react';
import { BiChevronRight } from 'react-icons/bi';
import { Link } from 'react-router-dom';

import { BreadcrumbFragment } from '../../graphql/generated';
import { navigateToDirectory } from '../../pages/file-browser-page/file-browser-page';

/**
 * Folder display name overrides — visual only, does not affect actual paths.
 * Scoped to specific cedant + breadcrumb path combinations.
 */
const FOLDER_DISPLAY_OVERRIDES: { cedant: string; pathMatch: string; displayName: string }[] = [
  { cedant: 'ALLIANZUS202112', pathMatch: 'ToAllianz-US/Investments', displayName: 'Investments - Do Not Use' },
];

const getBreadcrumbDisplayName = (
  name: string,
  cedant: string,
  breadcrumbs: BreadcrumbFragment[],
  index: number
): string => {
  // Build the path up to and including this breadcrumb
  const pathSegments = breadcrumbs.slice(0, index + 1).map((b) => b.name);
  const currentPath = pathSegments.join('/');

  const override = FOLDER_DISPLAY_OVERRIDES.find(
    (o) => o.cedant.toLowerCase() === cedant.toLowerCase() && currentPath.endsWith(o.pathMatch)
  );
  return override ? override.displayName : name;
};

type BreadcrumbsProps = {
  cedantId: string; // cedant id from the URL params
  breadcrumbs: BreadcrumbFragment[];
};

export const Breadcrumbs: FC<BreadcrumbsProps> = ({
  cedantId,
  breadcrumbs,
}) => {
  return (
    <ChakraBreadcrumb
      mb="8"
      spacing="2"
      separator={<BiChevronRight size={28} />}
      data-testid="breadcrumb-controller"
    >
      {breadcrumbs.map((crumb, index) => {
        const finalCrumb = index === breadcrumbs.length - 1;
        const uniqueKey = crumb.directoryId || `root-${crumb.name}`;

        return (
          <BreadcrumbItem
            color="gray.500"
            data-testid="crumb"
            key={uniqueKey}
            _first={{ textTransform: 'capitalize' }}
          >
            {finalCrumb ? (
              <Text
                color="gray.900"
                data-testid="active"
                textStyle="breadcrumb"
              >
                {getBreadcrumbDisplayName(crumb.name, cedantId, breadcrumbs, index)}
              </Text>
            ) : (
              <BreadcrumbLink
                as={Link}
                to={navigateToDirectory({
                  cedantId,
                  directoryId: crumb.directoryId || undefined,
                })}
              >
                <Text textStyle="breadcrumb">{getBreadcrumbDisplayName(crumb.name, cedantId, breadcrumbs, index)}</Text>
              </BreadcrumbLink>
            )}
          </BreadcrumbItem>
        );
      })}
    </ChakraBreadcrumb>
  );
};
